import { useState, useEffect, useCallback } from 'react';

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface UseSearchHistoryOptions {
  key?: string;
  max?: number;
  storage?: StorageLike;
}

interface UseSearchHistoryReturn {
  history: string[];
  addToHistory: (searchTerm: string) => void;
  removeFromHistory: (searchTerm: string) => void;
  clearHistory: () => void;
}

const HISTORY_KEY = 'search_history';
const MAX_HISTORY_ITEMS = 10;

const isBrowser = typeof window !== 'undefined';
const defaultStorage: StorageLike = isBrowser
  ? window.localStorage
  : { getItem: () => null, setItem: () => {}, removeItem: () => {} };

export function useSearchHistory({
  key = HISTORY_KEY,
  max = MAX_HISTORY_ITEMS,
  storage = defaultStorage,
}: UseSearchHistoryOptions = {}): UseSearchHistoryReturn {
  const [history, setHistory] = useState<string[]>([]);

  // Load history from localStorage on mount
  useEffect(() => {
    let savedHistory = null;
    try {
      savedHistory = storage.getItem(key);
    } catch (err) {
      console.error('Failed to access search history storage:', err);
    }
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (error) {
        console.error('Failed to parse search history:', error);
        setHistory([]);
      }
    }
  }, [key, storage]);

  // Save history to localStorage whenever it changes (debounced)
  useEffect(() => {
    if (!history) return;
    const handler = setTimeout(() => {
      try {
        storage.setItem(key, JSON.stringify(history));
      } catch (err) {
        console.error('Failed to persist search history:', err);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [history, key, storage]);

  useEffect(() => {
    if (!isBrowser) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        try {
          setHistory(JSON.parse(e.newValue));
        } catch (err) {
          console.error('Failed to parse storage event data:', err);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);

  const addToHistory = useCallback(
    (searchTerm: string) => {
      if (!searchTerm.trim()) return;

      setHistory((prev) => {
        const filtered = prev.filter((item) => item !== searchTerm);
        return [searchTerm, ...filtered].slice(0, max);
      });
    },
    [max],
  );

  const removeFromHistory = useCallback((searchTerm: string) => {
    setHistory((prev) => prev.filter((item) => item !== searchTerm));
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    storage.removeItem(key);
  }, [key, storage]);

  return {
    history,
    addToHistory,
    removeFromHistory,
    clearHistory,
  };
}
